/**
 * Derives the app's entire primary-role palette from one clinic-chosen hex.
 *
 * ── WHAT THIS REPLACES ────────────────────────────────────────────────────
 * `src/styles/color.css` §1 and §3 declare twelve values for the primary
 * role: a five-step brand ramp plus seven role tokens (fill, text, hover,
 * active, soft, border, on-fill). Hand-picking twelve values per clinic is
 * not a product, so this file reproduces exactly those twelve from one.
 *
 * The targets below are not invented — they are MEASURED off the original,
 * hand-tuned indigo palette (#4a52c4). `deriveBrandPalette('#4a52c4')` still
 * reproduces those hand-picked values to within a shade, which is the check
 * that the generator understands the design system rather than merely
 * resembling it. color.css now holds the generator's output for the
 * Dentacares pine (#0f766e). `pnpm brand:check` asserts both.
 *
 * ── THE RULES IT ENFORCES ─────────────────────────────────────────────────
 * 1. The clinic's colour is used LITERALLY as the fill. A picker that
 *    silently "corrects" the chosen colour is a picker nobody trusts.
 * 2. Anything that carries GLYPHS is contrast-checked and moved until it
 *    clears WCAG AA. color.css already draws this distinction by hand — the
 *    `-text` step exists precisely because several brand fills fail as text.
 *    A generated palette must keep that promise for hues nobody has vetted.
 * 3. The ramp is monotonic in lightness for every possible base, including
 *    near-white and near-black ones. Targets are expressed as a FRACTION of
 *    the distance to white or black rather than as absolute lightnesses,
 *    which is what makes that true.
 */

import {
  contrastRatio,
  ensureContrast,
  hexToOklch,
  isHexColor,
  oklchToHex,
  pickInkOn,
  type Oklch,
} from './color-space';

/**
 * The Dentacares pine. What a clinic that has chosen nothing sees, and the
 * value color.css §1/§3 are generated from.
 */
export const DEFAULT_BRAND_COLOR = '#0f766e';

/**
 * Backgrounds the generated palette is measured against. These mirror
 * `--surface-0` / `--surface-1` and the two ink anchors in color.css; they
 * are duplicated here because contrast is arithmetic and arithmetic cannot
 * read a CSS custom property.
 */
const SURFACE = {
  /** `--surface-0`, the page. */
  lightPage: '#fafafc',
  /** `--surface-1`, cards and fields — where brand text most often sits. */
  lightCard: '#ffffff',
  /** `--surface-0` under `data-theme="dark"`. */
  darkPage: '#14151a',
} as const;

const INK = {
  light: '#14151a',
  dark: '#f4f5f7',
  white: '#ffffff',
} as const;

/** WCAG 2.1 AA for body-sized text and icons. */
export const AA_TEXT = 4.5;
/** WCAG 2.1 AA for the boundary of a UI component (1.4.11). */
export const AA_NON_TEXT = 3;

/**
 * How far each step sits from the base, as a fraction.
 *
 * `lift` is the fraction of the remaining distance to `LIGHT_CEILING`;
 * `drop` is a multiple of the base lightness. Both are relative, so a base at
 * L 0.92 and a base at L 0.25 both produce an ordered ramp — an absolute
 * target like "step 400 is L 0.665" would invert for a pale base.
 *
 * `chroma` is a multiple of the base chroma, so a muted brand stays muted and
 * a grey one stays grey instead of being saturated into a colour the clinic
 * did not pick.
 *
 * Every fraction below was MEASURED off the shipped indigo, base L 0.4986 /
 * C 0.1753 / H 275.0:
 *
 *   step        shipped    L        C       lift     drop    C ÷ C(base)
 *   ramp 100    #e7e8fb    0.9359   0.0256  0.9276    —        0.146
 *   ramp 400    #8a90e8    0.6854   0.1290  0.3962    —        0.736
 *   ramp 600    #4a52c4    0.4986   0.1753    —      1.000     1.000
 *   ramp 800    #262c6e    0.3299   0.1131    —      0.662     0.646
 *   ramp 900    #141838    0.2253   0.0616    —      0.452     0.352
 *   border      #c3c6f2    0.8388   0.0614  0.7216    —        0.350
 *   hover       #3f46ab    0.4491   0.1592    —      0.901     0.909
 *   active      #363c93    0.4043   0.1410    —      0.811     0.805
 *
 * Hue is held constant. The shipped tints drift a few degrees bluer as they
 * lighten (275° → 283° at step 100), which is a designer's touch on one
 * specific hue rather than a rule that generalises — and at C 0.026 it is
 * below the threshold at which anyone could name the difference.
 */
const LIGHT_CEILING = 0.97;

const RAMP_STEPS = {
  100: { lift: 0.9276, chroma: 0.146 },
  400: { lift: 0.3962, chroma: 0.736 },
  800: { drop: 0.6616, chroma: 0.646 },
  900: { drop: 0.4517, chroma: 0.352 },
} as const;

/** `--color-primary-border`. */
const BORDER_STEP = { lift: 0.7216, chroma: 0.35 } as const;

/**
 * Interaction states, as multiples of base lightness and chroma. Relative
 * rather than absolute so a pale brand still darkens visibly on press
 * instead of bottoming out against a floor it already sits below.
 */
const HOVER_STEP = { drop: 0.9007, chroma: 0.909 } as const;
const ACTIVE_STEP = { drop: 0.8108, chroma: 0.805 } as const;

/** Dark-theme interaction states move the other way — lighter, not darker. */
const DARK_HOVER_LIFT = 0.05;
const DARK_ACTIVE_LIFT = 0.1;

/** `--color-primary-border` in dark: #3a4192 → L 0.4162 = L(800) × 1.2614. */
const DARK_BORDER_FACTOR = 1.2614;
const DARK_BORDER_CHROMA = 0.752;

function lift(base: Oklch, fraction: number, chroma: number): string {
  // The ceiling never sits BELOW the base. Without this, a base lighter than
  // LIGHT_CEILING — a clinic picking near-white — produces "tints" that are
  // darker than the colour they tint, inverting the ramp. Clamping makes the
  // ramp collapse toward a single value instead, which is degenerate but
  // still ordered; the settings page warns before it gets that far.
  const ceiling = Math.max(LIGHT_CEILING, base.l);
  return oklchToHex({
    l: base.l + (ceiling - base.l) * fraction,
    c: base.c * chroma,
    h: base.h,
  });
}

function drop(base: Oklch, factor: number, chroma: number): string {
  return oklchToHex({ l: base.l * factor, c: base.c * chroma, h: base.h });
}

function shiftLightness(hex: string, delta: number): string {
  const ok = hexToOklch(hex);
  return ok ? oklchToHex({ ...ok, l: ok.l + delta }) : hex;
}

/** The five brand-ramp anchors that `styles/theme.css` builds 50–900 from. */
export interface BrandRamp {
  100: string;
  400: string;
  600: string;
  800: string;
  900: string;
}

/** The seven role tokens color.css §3 declares for `primary`. */
export interface BrandRole {
  /** `--color-primary` — the fill. The clinic's colour, untouched. */
  primary: string;
  /** `--color-primary-text` — the same colour, guaranteed readable as text. */
  primaryText: string;
  hover: string;
  active: string;
  /** `--color-primary-soft` — tinted background. */
  soft: string;
  border: string;
  /** `--color-primary-on` — glyphs drawn on the fill. */
  on: string;
}

export interface ContrastReport {
  /** The fill against the page. Below 4.5 is why `primaryText` differs. */
  fillAsTextOnPage: number;
  /** The same on a card — the stricter of the two, and the one that matters. */
  fillAsTextOnCard: number;
  /** `primaryText` on a card. Guaranteed ≥ 4.5 unless sRGB made it impossible. */
  textOnCard: number;
  /** `on` against the fill — a button's own label. */
  inkOnFill: number;
  /** True when the chosen colour was usable as text without being darkened. */
  fillIsReadableAsText: boolean;
  /** True when every generated glyph pairing clears AA. */
  meetsAA: boolean;
}

export interface BrandPalette {
  /** The stored value, normalised to lower-case `#rrggbb`. */
  base: string;
  ramp: BrandRamp;
  light: BrandRole;
  dark: BrandRole;
  report: ContrastReport;
}

/**
 * Builds the full palette for a brand colour.
 *
 * Falls back to the Dentacares pine for anything that is not a 6-digit hex
 * rather than throwing: this runs on a value that arrives over the network,
 * and a malformed one should cost the clinic its branding, not its dashboard.
 */
export function deriveBrandPalette(input: string): BrandPalette {
  const base = (isHexColor(input) ? input : DEFAULT_BRAND_COLOR).toLowerCase();
  const ok = hexToOklch(base) as Oklch;

  const ramp: BrandRamp = {
    100: lift(ok, RAMP_STEPS[100].lift, RAMP_STEPS[100].chroma),
    400: lift(ok, RAMP_STEPS[400].lift, RAMP_STEPS[400].chroma),
    600: base,
    800: drop(ok, RAMP_STEPS[800].drop, RAMP_STEPS[800].chroma),
    900: drop(ok, RAMP_STEPS[900].drop, RAMP_STEPS[900].chroma),
  };

  // ── Light theme ─────────────────────────────────────────────────────────
  // Text is measured against --surface-1 (pure white), not --surface-0. The
  // note on --text-placeholder in color.css is the precedent: fields and
  // cards render on white, and a value that passes only against the very
  // slightly grey page fails where it is actually read.
  const lightText = ensureContrast(base, SURFACE.lightCard, AA_TEXT);
  const lightInk = pickInkOn(base, [INK.white, INK.light]);

  const light: BrandRole = {
    primary: base,
    primaryText: lightText,
    hover: drop(ok, HOVER_STEP.drop, HOVER_STEP.chroma),
    active: drop(ok, ACTIVE_STEP.drop, ACTIVE_STEP.chroma),
    soft: ramp[100],
    border: lift(ok, BORDER_STEP.lift, BORDER_STEP.chroma),
    on: lightInk.color,
  };

  // ── Dark theme ──────────────────────────────────────────────────────────
  // color.css promotes primary to the 400 step in dark, because a mid-dark
  // brand fill has no luminance left to spend on a dark ground. Starting from
  // 400 and then enforcing contrast reproduces that rule for any hue.
  const darkPrimary = ensureContrast(ramp[400], SURFACE.darkPage, AA_TEXT);
  const darkInk = pickInkOn(darkPrimary, [INK.dark, INK.light]);

  const dark: BrandRole = {
    primary: darkPrimary,
    primaryText: darkPrimary,
    hover: shiftLightness(darkPrimary, DARK_HOVER_LIFT),
    active: shiftLightness(darkPrimary, DARK_ACTIVE_LIFT),
    // The 800 step is the dark theme's tinted background, the way --indigo-800
    // is in color.css — dark enough to sit behind --text-primary at 12:1.
    soft: ramp[800],
    border: oklchToHex({
      l: (hexToOklch(ramp[800]) as Oklch).l * DARK_BORDER_FACTOR,
      c: ok.c * DARK_BORDER_CHROMA,
      h: ok.h,
    }),
    on: darkInk.color,
  };

  const fillAsTextOnCard = contrastRatio(base, SURFACE.lightCard);
  const report: ContrastReport = {
    fillAsTextOnPage: contrastRatio(base, SURFACE.lightPage),
    fillAsTextOnCard,
    textOnCard: contrastRatio(lightText, SURFACE.lightCard),
    inkOnFill: lightInk.ratio,
    fillIsReadableAsText: fillAsTextOnCard >= AA_TEXT,
    // The fill itself is allowed to fail as text — that is what primaryText is
    // for. What must not fail is a pairing the app actually renders: the
    // derived ink on the fill, and the derived text colour on a card.
    meetsAA:
      lightInk.ratio >= AA_TEXT &&
      contrastRatio(lightText, SURFACE.lightCard) >= AA_TEXT,
  };

  return { base, ramp, light, dark, report };
}
